import { ComponentType, ReactElement, useId } from 'react';
import {
  AddInline,
  FormInlineError,
  FormInlineMemberError,
  Inlines,
  RemoveInline,
  TableInlineColumn,
  TableInlines,
} from 'components';
import { Box, Icon, Stack } from '@ff-it/ui';
import * as columns from './rateColumns';
import { GroupFieldsProps, AVBGroupFields, CVBGroupFields } from './GroupFields';
import { Tooltip } from 'react-tooltip';

type BonusType = 'AVB' | 'CVB';

const configMap: Record<
  BonusType,
  {
    initialGroupValues: any;
    initialRateValues: any;
    GroupFields: ComponentType<GroupFieldsProps>;
    columns: TableInlineColumn[];
  }
> = {
  AVB: {
    initialGroupValues: {
      provider: null,
      type: null,
    },
    initialRateValues: {
      category: null,
      activity: null,
      outlet_or_service_category: null,
      client: null,
      payer: null,
      current: null,
      target: null,
      final: null,
    },
    columns: columns.avb,
    GroupFields: AVBGroupFields,
  },
  CVB: {
    initialGroupValues: {
      client: null,
      payer: null,
      type: null,
    },
    initialRateValues: {
      category: null,
      activity: null,
      outlet_or_service_category: null,
      provider: null,
      current: null,
      target: null,
      final: null,
    },
    columns: columns.cvb,
    GroupFields: CVBGroupFields,
  },
};

export function RateNotice(): ReactElement {
  const id = useId();

  return (
    <Box ml="auto">
      <Tooltip id={id}>
        <Box as="ul" m="0" pl="2">
          <li>Groups have to be unique</li>
          <li>Rows that have more specifc conditions are applied first</li>
          <li>
            0 is value that will set zero invoicable demand <br />
            (not the same thing as empty value)
          </li>
        </Box>
      </Tooltip>
      Rates <Icon icon="circle-question" data-tooltip-id={id} />
    </Box>
  );
}

export function RateGroups({ bonusType }: { bonusType: BonusType }): ReactElement {
  const { columns, GroupFields, initialGroupValues, initialRateValues } = configMap[bonusType];
  return (
    <Box testId="groups" mb="5">
      <Box display="flex" alignItems="center" fontWeight="semibold" mb="2">
        Rate groups
        <Box marginLeft="auto" fontWeight="regular" fontSize="xs">
          <RateNotice />
        </Box>
      </Box>
      <FormInlineError name="groups" />

      <Inlines name="groups">
        {({ name, index }) => (
          <Box>
            <Box background="gray100" px="2" py="1" border="subtle" style={{ borderBottom: 'none' }}>
              <Stack direction="horizontal" align="center">
                <GroupFields name={name} />
                <RemoveInline name="groups" index={index} ml="auto" size="xs" variant="secondary-danger">
                  Remove group
                </RemoveInline>
              </Stack>
              <FormInlineMemberError name={name} className="mb-0 mt-1" />
            </Box>

            <TableInlines
              size="xs"
              headerVariant="default"
              sortable={false}
              name={`${name}.rates`}
              initialValues={initialRateValues}
              columns={columns}
              min={1}
            />
          </Box>
        )}
      </Inlines>

      <AddInline
        name="groups"
        size="sm"
        initialValues={() => ({
          ...initialGroupValues,
          rates: [{ ...initialRateValues, __NEW__: Date.now() }],
          __NEW__: Date.now(),
        })}
      >
        Add group
      </AddInline>
    </Box>
  );
}
